import { useState } from 'react';
import moment from 'moment';
import FileDownload from 'js-file-download';
import { useLoading } from '../../../../hooks/useLoading';
import { useNotifications } from '../../../../hooks/useNotifications';
import { downloadTestingSheet } from '../../../../constants/requests';

/**
 * Custom hook for handling Testing component download operations
 * Manages download modal state, file selection, and download execution
 *
 * @param {Object} RCMData - The RCM data to download
 * @returns {Object} - Download functionality and state
 */
export const useTestingDownload = (RCMData) => {
  const { showNotifications } = useNotifications();
  const { request } = useLoading();

  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const getExtension = (contentType) => {
    switch (contentType) {
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return 'xlsx';
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'docx';
      default:
        return 'xlsx';
    }
  };

  const getFormattedDate = () => {
    return moment().format('D_MMM_YYYY_h_mm_ss_a');
  };

  const handleOpenDownload = () => {
    setOpenDownloadModal(true);
  };

  const handleCloseDownload = () => {
    setOpenDownloadModal(false);
    setSelectedFile(null);
    setErrorMessage(null);
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setErrorMessage(null);
  };

  const handleDownload = async () => {
    if (selectedFile === null) {
      setErrorMessage('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('data', JSON.stringify(RCMData.rows));

    try {
      const response = await request(() => downloadTestingSheet(formData));
      if (response?.response?.data?.statusCode) {
        showNotifications('error', response.response.data.message, 5000);
      } else {
        FileDownload(
          response.data,
          `testing_${getFormattedDate()}.${getExtension(
            response.headers['content-type']
          )}`
        );
        showNotifications('success', 'Downloaded successfully', 5000);
        handleCloseDownload();
      }
    } catch (error) {
      console.log(error);
      showNotifications('error', 'Download failed', 5000);
    }
  };

  return {
    openDownloadModal,
    errorMessage,
    selectedFile,
    handleOpenDownload,
    handleCloseDownload,
    handleDownload,
    handleFileSelect
  };
};
